import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Flex,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  Input,
  Select,
  HStack,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Spinner,
  Center,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Card,
  CardBody,
  Divider,
  useDisclosure,
  useToast,
  useColorModeValue,
  Textarea,
  VStack,
  Checkbox,
  Switch,
} from '@chakra-ui/react';
import {
  EditIcon, DeleteIcon, CheckIcon, StarIcon, AddIcon,
  CopyIcon, ArrowUpIcon, ArrowDownIcon, ChevronLeftIcon,
} from '@chakra-ui/icons';
import { getAdminUsers, createAdminUser, updateAdminUser, deleteAdminUser, assignUserToSite, setUserStatus } from 'services/adminUsers';
import { getRoles, createRole, updateRole, deleteRole, getPermissionsCatalog } from 'services/roles';
import { getSites, getSiteConfig, saveSiteConfig } from 'utils/site';
import { makeAPIRequest } from 'services/surveyService';
import { clearCategoriesCache } from 'services/surveyCategoriesService';
import { loadFormSyncConfigs, saveFormSyncConfig, updateFormSyncConfig, deleteFormSyncConfig } from 'services/formSync';

// ─── Constantes usuarios ──────────────────────────────────────────────────────

const DEFAULT_SITE_KEY = 'default_site_name';

// Fallback mientras carga el catálogo real desde /api/roles (Sistema > Roles).
// Las tarjetas "administrador"/"profesor"/"user" son roles del sistema — siempre existen en DB.
const FALLBACK_ROLES = [
  { value: 'administrador', label: 'Administrador', color: 'red',  isSystem: true, permissions: [] },
  { value: 'profesor',      label: 'Profesor',      color: 'blue', isSystem: true, permissions: [] },
];

const normalizeApiRole = (r) => ({
  id:          r.id,
  value:       r.slug,
  label:       r.label,
  color:       r.color || 'gray',
  isSystem:    !!r.is_system,
  permissions: r.permissions || [],
});

const getRoleLabel = (roles, roleValue) => {
  const found = (roles || []).find((r) => r.value === roleValue);
  return found ? found.label : roleValue;
};

const getRoleColor = (roles, roleValue) =>
  (roles || []).find((r) => r.value === roleValue)?.color || 'gray';

const STATUS_COLORS = {
  active:    'green',
  suspended: 'red',
  inactive:  'gray',
};

const STATUS_LABELS = {
  active:    'Activo',
  suspended: 'Suspendido',
  inactive:  'Inactivo',
};

// ─── Constantes formularios ───────────────────────────────────────────────────

// Fallback mientras carga desde la API
const DEFAULT_CATEGORIES_CONFIG = [
  { id: null, value: 'matriculas', label: 'Matrícula',  color: 'blue',   sort_order: 1 },
  { id: null, value: 'salud',      label: 'Salud',       color: 'green',  sort_order: 2 },
  { id: null, value: 'atencion',   label: 'Atención',    color: 'purple', sort_order: 3 },
  { id: null, value: 'documentos', label: 'Documentos',  color: 'orange', sort_order: 4 },
  { id: null, value: 'becas',      label: 'Becas',       color: 'yellow', sort_order: 5 },
  { id: null, value: 'casos',      label: 'Casos',       color: 'red',    sort_order: 6 },
  { id: null, value: 'otros',      label: 'Otros',       color: 'gray',   sort_order: 7 },
];

// Normaliza la respuesta de la API al shape que usan los componentes
const normalizeApiCategory = (cat) => ({
  id:         cat.id,
  value:      cat.slug,
  label:      cat.label,
  color:      cat.color || 'gray',
  sort_order: cat.sort_order ?? 0,
  roleAccess: cat.role_access || {},
});

const COLOR_OPTIONS = [
  { value: 'blue',   label: 'Azul'          },
  { value: 'green',  label: 'Verde'          },
  { value: 'purple', label: 'Morado'         },
  { value: 'orange', label: 'Naranja'        },
  { value: 'yellow', label: 'Amarillo'       },
  { value: 'red',    label: 'Rojo'           },
  { value: 'teal',   label: 'Verde azulado'  },
  { value: 'pink',   label: 'Rosa'           },
  { value: 'gray',   label: 'Gris'           },
];

const emptyCategory = () => ({
  value: '',
  label: '',
  color: 'gray',
  roleAccess: {
    administrador: { view: true, fill: true  },
    profesor:      { view: false, fill: false },
  },
});

const QUESTION_TYPES = [
  { value: 'text',      label: 'Texto corto' },
  { value: 'textarea',  label: 'Texto largo' },
  { value: 'radio',     label: 'Opción única (radio)' },
  { value: 'select',    label: 'Lista desplegable' },
  { value: 'checkbox',  label: 'Selección múltiple' },
  { value: 'date',      label: 'Fecha' },
  { value: 'number',    label: 'Número' },
  { value: 'email',     label: 'Email' },
  { value: 'file',      label: 'Archivo' },
  { value: 'signature', label: 'Firma' },
  { value: 'title',     label: 'Título / Separador' },
];

const COLUMN_WIDTHS = [
  { value: 12, label: 'Ancho completo' },
  { value: 6,  label: '½ ancho (2 por fila)' },
  { value: 4,  label: '⅓ ancho (3 por fila)' },
  { value: 3,  label: '¼ ancho (4 por fila)' },
];

const HAS_CHOICES = ['radio', 'select', 'checkbox'];

const emptyQuestion = () => ({
  text: '', type: 'text', subtext: '', column_width: 12, response_choices_text: '',
});
const emptySection  = () => ({ section_name: '', questions: [emptyQuestion()] });
const emptyForm     = () => ({
  survey_name: '', category: 'matriculas', description: '', sections: [emptySection()],
});

// ─── Helpers formularios ──────────────────────────────────────────────────────

const buildPayload = (form) => ({
  survey_name: form.survey_name.trim(),
  category:    form.category,
  description: form.description.trim(),
  sections: form.sections.map((sec) => ({
    section_name: sec.section_name.trim(),
    questions: sec.questions.map((q) => ({
      ...(q.id ? { id: q.id } : {}),
      text:         q.text.trim(),
      type:         q.type,
      subtext:      q.subtext.trim(),
      column_width: Number(q.column_width),
      response_choices: HAS_CHOICES.includes(q.type)
        ? q.response_choices_text.split('\n').map((s) => s.trim()).filter(Boolean)
        : [],
    })),
  })),
});

const apiSectionsToForm = (sections) =>
  Object.entries(sections || {}).map(([sectionName, questions]) => ({
    section_name: sectionName,
    questions: [...questions]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((q) => ({
        id:                    q.id || null,
        text:                  q.text || '',
        type:                  q.type?.name || 'text',
        subtext:               q.subtext || '',
        column_width:          q.column_width ?? 12,
        response_choices_text: (q.response_choices || q.responseChoices || []).map((c) => c.text).join('\n'),
      })),
  }));

// ─── Panel: Configuración de sitio ───────────────────────────────────────────

const SiteConfigPanel = () => {
  const toast = useToast();
  const [sites, setSites]             = useState([]);
  const [activeSite, setActiveSite]   = useState(null);
  const [defaultSite, setDefaultSite] = useState(localStorage.getItem(DEFAULT_SITE_KEY) || '');

  useEffect(() => {
    try {
      const rawSites  = getSites();
      const rawConfig = getSiteConfig();
      if (rawSites)  setSites(JSON.parse(rawSites));
      if (rawConfig) setActiveSite(JSON.parse(rawConfig));
    } catch (e) {
      console.error('Error leyendo configuración de sitio:', e);
    }
  }, []);

  const handleActivate = (site) => {
    saveSiteConfig(JSON.stringify(site));
    setActiveSite(site);
    toast({ title: `Sitio "${site.name}" activado`, status: 'success', duration: 2000 });
  };

  const handleSetDefault = (siteName) => {
    localStorage.setItem(DEFAULT_SITE_KEY, siteName);
    setDefaultSite(siteName);
    toast({
      title: `"${siteName}" marcado como predeterminado`,
      description: 'Se cargará automáticamente en el próximo inicio de sesión',
      status: 'info',
      duration: 3000,
    });
  };

  if (sites.length === 0) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        No hay sitios disponibles en esta sesión.
      </Alert>
    );
  }

  return (
    <Box>
      <Text fontSize="sm" color="gray.500" mb={4}>
        Sitio activo actualmente:{' '}
        <Badge colorScheme="teal" fontSize="sm">{activeSite?.name || '—'}</Badge>
        {defaultSite && (
          <Text as="span" ml={3} fontSize="xs" color="gray.400">
            Predeterminado: <strong>{defaultSite}</strong>
          </Text>
        )}
      </Text>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
        {sites.map((site) => {
          const isActive  = activeSite?.name === site.name;
          const isDefault = defaultSite === site.name;
          const configs   = site.configs || [];

          return (
            <Box
              key={site.name}
              borderWidth="2px"
              borderColor={isActive ? 'teal.400' : 'gray.200'}
              borderRadius="lg"
              p={4}
              bg={isActive ? 'teal.50' : 'white'}
              position="relative"
            >
              {isDefault && (
                <Badge colorScheme="yellow" position="absolute" top={2} right={2} fontSize="xs">
                  Predeterminado
                </Badge>
              )}
              <Text fontWeight="bold" mb={1} pr={isDefault ? 24 : 0}>{site.name}</Text>
              <Box mb={3}>
                {configs.slice(0, 3).map((c) => (
                  <Text key={c.key} fontSize="xs" color="gray.500" noOfLines={1}>
                    <Text as="span" fontWeight="medium">{c.key}:</Text>{' '}
                    {c.key.includes('SECRET') || c.key.includes('KEY') ? '••••••' : c.value}
                  </Text>
                ))}
                {configs.length > 3 && (
                  <Text fontSize="xs" color="gray.400">+{configs.length - 3} configuraciones más</Text>
                )}
              </Box>
              <Divider mb={3} />
              <HStack spacing={2}>
                <Button
                  size="xs" colorScheme={isActive ? 'teal' : 'gray'}
                  leftIcon={isActive ? <CheckIcon /> : undefined}
                  onClick={() => !isActive && handleActivate(site)}
                  isDisabled={isActive} flex={1}
                >
                  {isActive ? 'Activo' : 'Activar'}
                </Button>
                <Button
                  size="xs" colorScheme={isDefault ? 'yellow' : 'gray'}
                  variant={isDefault ? 'solid' : 'outline'} leftIcon={<StarIcon />}
                  onClick={() => !isDefault && handleSetDefault(site.name)}
                  isDisabled={isDefault} flex={1}
                >
                  {isDefault ? 'Predeter.' : 'Predeterminar'}
                </Button>
              </HStack>
            </Box>
          );
        })}
      </SimpleGrid>
    </Box>
  );
};

// ─── Formulario de usuario (crear / editar) ───────────────────────────────────

const UserForm = ({ initialData, onSubmit, loading, error, isEdit, roles }) => {
  const [form, setForm] = useState({
    first_name: initialData?.first_name || '',
    last_name:  initialData?.last_name  || '',
    email:      initialData?.email      || '',
    username:   initialData?.username   || '',
    password:   '',
    role:       initialData?.role       || 'user',
  });

  useEffect(() => {
    setForm({
      first_name: initialData?.first_name || '',
      last_name:  initialData?.last_name  || '',
      email:      initialData?.email      || '',
      username:   initialData?.username   || '',
      password:   '',
      role:       initialData?.role       || 'user',
    });
  }, [initialData]);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <Box>
      {error && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />{error}
        </Alert>
      )}
      <HStack mb={3}>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Nombre</FormLabel>
          <Input size="sm" value={form.first_name} onChange={set('first_name')} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Apellido</FormLabel>
          <Input size="sm" value={form.last_name} onChange={set('last_name')} />
        </FormControl>
      </HStack>
      <FormControl isRequired mb={3}>
        <FormLabel fontSize="sm">Email</FormLabel>
        <Input size="sm" type="email" value={form.email} onChange={set('email')} />
      </FormControl>
      <FormControl isRequired={!isEdit} mb={3}>
        <FormLabel fontSize="sm">Nombre de usuario</FormLabel>
        <Input size="sm" value={form.username} onChange={set('username')} isReadOnly={isEdit} bg={isEdit ? 'gray.50' : undefined} />
      </FormControl>
      <FormControl isRequired={!isEdit} mb={3}>
        <FormLabel fontSize="sm">
          Contraseña{' '}
          {isEdit && <Text as="span" color="gray.400">(dejar en blanco para no cambiar)</Text>}
        </FormLabel>
        <Input size="sm" type="password" value={form.password} onChange={set('password')} />
      </FormControl>
      <FormControl isRequired mb={4}>
        <FormLabel fontSize="sm">Rol</FormLabel>
        <Select size="sm" value={form.role} onChange={set('role')}>
          {(roles || []).map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </Select>
      </FormControl>
      <Button colorScheme="blue" width="100%" onClick={() => onSubmit(form)} isLoading={loading}>
        {isEdit ? 'Guardar cambios' : 'Crear usuario'}
      </Button>
    </Box>
  );
};

// ─── Editor de pregunta ───────────────────────────────────────────────────────

const QuestionEditor = ({ question, onChange, onRemove, onMoveUp, onMoveDown, index, total }) => {
  // Cambios en texto, tipo o subtext significan semánticamente una pregunta nueva:
  // el ID se limpia para que el backend cree una nueva en vez de reutilizar la anterior.
  // column_width y reorder no son cambios semánticos — preservan el ID.
  const SEMANTIC_FIELDS = ['text', 'type', 'subtext'];
  const set = (field) => (e) => {
    const updated = { ...question, [field]: e.target.value };
    if (SEMANTIC_FIELDS.includes(field)) updated.id = null;
    onChange(updated);
  };

  return (
    <Box borderWidth="1px" borderRadius="md" p={3} bg="gray.50" mb={2}>
      <Flex align="center" mb={2} gap={2} flexWrap="wrap">
        <Text fontSize="xs" color="gray.400" minW="24px">#{index + 1}</Text>
        <Select size="xs" value={question.type} onChange={set('type')} maxW="180px" bg="white">
          {QUESTION_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </Select>
        <Select
          size="xs" maxW="190px" bg="white"
          value={question.column_width}
          onChange={(e) => onChange({ ...question, column_width: Number(e.target.value) })}
        >
          {COLUMN_WIDTHS.map((w) => (
            <option key={w.value} value={w.value}>{w.label}</option>
          ))}
        </Select>
        <HStack ml="auto" spacing={1}>
          <IconButton icon={<ArrowUpIcon />} size="xs" variant="ghost"
            isDisabled={index === 0} onClick={onMoveUp} aria-label="Subir" />
          <IconButton icon={<ArrowDownIcon />} size="xs" variant="ghost"
            isDisabled={index === total - 1} onClick={onMoveDown} aria-label="Bajar" />
          <IconButton icon={<DeleteIcon />} size="xs" variant="ghost"
            colorScheme="red" onClick={onRemove} aria-label="Eliminar pregunta" />
        </HStack>
      </Flex>

      <FormControl mb={2}>
        <Input size="sm" placeholder="Texto de la pregunta *" bg="white"
          value={question.text} onChange={set('text')} />
      </FormControl>
      <FormControl mb={HAS_CHOICES.includes(question.type) ? 2 : 0}>
        <Input size="sm" placeholder="Subtexto / instrucción (opcional)" bg="white"
          value={question.subtext} onChange={set('subtext')} />
      </FormControl>

      {HAS_CHOICES.includes(question.type) && (
        <FormControl>
          <FormLabel fontSize="xs" color="gray.500" mb={1}>Opciones (una por línea)</FormLabel>
          <Textarea size="sm" rows={3} bg="white"
            placeholder={'Opción 1\nOpción 2\nOpción 3'}
            value={question.response_choices_text}
            onChange={set('response_choices_text')} />
        </FormControl>
      )}
    </Box>
  );
};

// ─── Editor de sección ────────────────────────────────────────────────────────

const SectionEditor = ({ section, onChange, onRemove, index, total, onMoveUp, onMoveDown }) => {
  const updateQuestion = (qIdx, updated) => {
    const questions = [...section.questions];
    questions[qIdx] = updated;
    onChange({ ...section, questions });
  };
  const removeQuestion = (qIdx) =>
    onChange({ ...section, questions: section.questions.filter((_, i) => i !== qIdx) });
  const moveQuestion = (qIdx, dir) => {
    const questions = [...section.questions];
    const target = qIdx + dir;
    if (target < 0 || target >= questions.length) return;
    [questions[qIdx], questions[target]] = [questions[target], questions[qIdx]];
    onChange({ ...section, questions });
  };
  const addQuestion = () =>
    onChange({ ...section, questions: [...section.questions, emptyQuestion()] });

  return (
    <Box borderWidth="2px" borderColor="blue.100" borderRadius="lg" p={4} mb={4}>
      <Flex align="center" mb={3} gap={2}>
        <Text fontWeight="bold" fontSize="sm" color="blue.600" minW="80px">
          Sección {index + 1}
        </Text>
        <Input
          size="sm" placeholder="Nombre de la sección *" flex={1}
          value={section.section_name}
          onChange={(e) => onChange({ ...section, section_name: e.target.value })}
        />
        <HStack spacing={1}>
          <IconButton icon={<ArrowUpIcon />} size="xs" variant="ghost"
            isDisabled={index === 0} onClick={onMoveUp} aria-label="Subir sección" />
          <IconButton icon={<ArrowDownIcon />} size="xs" variant="ghost"
            isDisabled={index === total - 1} onClick={onMoveDown} aria-label="Bajar sección" />
          <IconButton icon={<DeleteIcon />} size="xs" variant="ghost" colorScheme="red"
            onClick={onRemove} aria-label="Eliminar sección" isDisabled={total === 1} />
        </HStack>
      </Flex>

      {section.questions.map((q, qIdx) => (
        <QuestionEditor
          key={qIdx} index={qIdx} total={section.questions.length}
          question={q}
          onChange={(updated) => updateQuestion(qIdx, updated)}
          onRemove={() => removeQuestion(qIdx)}
          onMoveUp={() => moveQuestion(qIdx, -1)}
          onMoveDown={() => moveQuestion(qIdx, 1)}
        />
      ))}

      <Button size="xs" leftIcon={<AddIcon />} variant="ghost" colorScheme="blue"
        onClick={addQuestion} mt={1}>
        + Agregar pregunta
      </Button>
    </Box>
  );
};

// ─── Vista de edición de formulario ──────────────────────────────────────────

const SurveyEditorForm = ({ initialForm, onSave, onCancel, loading, saveError, categories }) => {
  const [form, setForm] = useState(initialForm || emptyForm());
  const cats = categories?.length ? categories : DEFAULT_CATEGORIES_CONFIG;

  useEffect(() => { setForm(initialForm || emptyForm()); }, [initialForm]);

  const setField = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const updateSection = (sIdx, updated) => {
    const sections = [...form.sections];
    sections[sIdx] = updated;
    setForm((prev) => ({ ...prev, sections }));
  };
  const removeSection = (sIdx) =>
    setForm((prev) => ({ ...prev, sections: prev.sections.filter((_, i) => i !== sIdx) }));
  const moveSection = (sIdx, dir) => {
    const sections = [...form.sections];
    const target = sIdx + dir;
    if (target < 0 || target >= sections.length) return;
    [sections[sIdx], sections[target]] = [sections[target], sections[sIdx]];
    setForm((prev) => ({ ...prev, sections }));
  };
  const addSection = () =>
    setForm((prev) => ({ ...prev, sections: [...prev.sections, emptySection()] }));

  const isValid =
    form.survey_name.trim() &&
    form.sections.length > 0 &&
    form.sections.every((s) => s.section_name.trim() && s.questions.length > 0 &&
      s.questions.every((q) => q.text.trim()));

  return (
    <Box>
      {saveError && (
        <Alert status="error" mb={4} borderRadius="md"><AlertIcon />{saveError}</Alert>
      )}

      {/* Datos generales */}
      <Box borderWidth="1px" borderRadius="lg" p={4} mb={4} bg="white">
        <Text fontWeight="bold" fontSize="sm" mb={3} color="gray.600">Datos del formulario</Text>
        <HStack mb={3} align="flex-start">
          <FormControl isRequired flex={2}>
            <FormLabel fontSize="sm">Nombre del formulario</FormLabel>
            <Input size="sm" value={form.survey_name} onChange={setField('survey_name')}
              placeholder="Ej: Ficha de matrícula 2025" />
          </FormControl>
          <FormControl isRequired flex={1}>
            <FormLabel fontSize="sm">Categoría</FormLabel>
            <Select size="sm" value={form.category} onChange={setField('category')}>
              {cats.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </Select>
          </FormControl>
        </HStack>
        <FormControl>
          <FormLabel fontSize="sm">Descripción (opcional)</FormLabel>
          <Input size="sm" value={form.description} onChange={setField('description')}
            placeholder="Descripción breve del formulario" />
        </FormControl>
      </Box>

      {/* Secciones */}
      <Text fontWeight="bold" fontSize="sm" mb={3} color="gray.600">Secciones y preguntas</Text>
      {form.sections.map((sec, sIdx) => (
        <SectionEditor
          key={sIdx} index={sIdx} total={form.sections.length}
          section={sec}
          onChange={(updated) => updateSection(sIdx, updated)}
          onRemove={() => removeSection(sIdx)}
          onMoveUp={() => moveSection(sIdx, -1)}
          onMoveDown={() => moveSection(sIdx, 1)}
        />
      ))}
      <Button size="sm" leftIcon={<AddIcon />} variant="outline" colorScheme="blue"
        onClick={addSection} mb={6}>
        + Agregar sección
      </Button>

      <Divider mb={4} />
      <HStack justify="flex-end" spacing={3}>
        <Button size="sm" variant="ghost" onClick={onCancel} isDisabled={loading}>Cancelar</Button>
        <Button size="sm" colorScheme="blue" isLoading={loading} isDisabled={!isValid || loading}
          onClick={() => onSave(buildPayload(form))}>
          Guardar formulario
        </Button>
      </HStack>
    </Box>
  );
};

// ─── Panel: Formularios ───────────────────────────────────────────────────────

const FormulariosPanel = () => {
  const toast = useToast();
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES_CONFIG);
  const categoryColorMap = useMemo(() => Object.fromEntries(categories.map(c => [c.value, c.color])), [categories]);

  useEffect(() => {
    makeAPIRequest('/survey-categories', 'get')
      .then((data) => setCategories((Array.isArray(data) ? data : []).map(normalizeApiCategory)))
      .catch(() => {});
  }, []);

  // Vista: 'list' | 'edit'
  const [view, setView]                     = useState('list');
  const [editingSurvey, setEditingSurvey]   = useState(null);
  const [editorInitialForm, setEditorInitialForm] = useState(null);
  const [editorLoading, setEditorLoading]   = useState(false);
  const [saveLoading, setSaveLoading]       = useState(false);
  const [saveError, setSaveError]           = useState('');

  const [surveys, setSurveys]       = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError]   = useState('');
  const [search, setSearch]         = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Modal eliminar
  const { isOpen: isDeleteOpen, onOpen: openDelete, onClose: closeDelete } = useDisclosure();
  const [deletingSurvey, setDeletingSurvey]   = useState(null);
  const [deleteLoading, setDeleteLoading]     = useState(false);

  // Sitio activo — cada survey trae su relación `sites` (SurveyController@index hace
  // Survey::with('sites')), así que detectar huérfanos no requiere llamadas extra.
  const activeSite = useMemo(() => JSON.parse(getSiteConfig() || '{}'), []);
  const [fixingOrphans, setFixingOrphans] = useState(false);
  const orphanedSurveys = useMemo(
    () => activeSite?.slug
      ? surveys.filter((s) => !(s.sites || []).some((site) => site.slug === activeSite.slug))
      : [],
    [surveys, activeSite]
  );

  // Cargar lista
  const fetchSurveys = useCallback(async () => {
    setListLoading(true);
    setListError('');
    try {
      const data = await makeAPIRequest('/surveys', 'get');
      setSurveys(Array.isArray(data) ? data : []);
    } catch (err) {
      setListError(err?.response?.data?.message || err.message || 'Error al cargar formularios');
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => { fetchSurveys(); }, [fetchSurveys]);

  // Nuevo formulario
  const handleNewSurvey = () => {
    setEditingSurvey(null);
    setEditorInitialForm(emptyForm());
    setSaveError('');
    setView('edit');
  };

  // Editar existente
  const handleEditSurvey = async (survey) => {
    setEditorLoading(true);
    setSaveError('');
    setView('edit');
    setEditingSurvey(survey);
    setEditorInitialForm(null);
    try {
      const data = await makeAPIRequest(`/surveys/${survey.slug || survey.id}`, 'get');
      setEditorInitialForm({
        survey_name: data.survey?.name || survey.name || '',
        category:    data.survey?.category || survey.category || 'matriculas',
        description: data.survey?.description || survey.description || '',
        sections:    apiSectionsToForm(data.sections),
      });
    } catch (err) {
      toast({ title: 'Error al cargar formulario', description: err.message, status: 'error', duration: 4000 });
      setView('list');
    } finally {
      setEditorLoading(false);
    }
  };

  // Guardar (crear o actualizar)
  const handleSave = async (payload) => {
    setSaveLoading(true);
    setSaveError('');
    try {
      if (editingSurvey) {
        await makeAPIRequest(`/survey/${editingSurvey.id}`, 'put', {}, payload);
        toast({ title: 'Formulario actualizado', status: 'success', duration: 3000 });
      } else {
        // Crear y adjuntar al sitio activo en una sola llamada atómica
        // (sites/{site}/surveys → SurveyController@createSurvey hace store()+attach()).
        // Antes esto eran 2 llamadas separadas: si la segunda (attachToSite) se saltaba
        // silenciosamente por falta de site_id, el survey quedaba huérfano — existía en
        // la BD pero invisible en el dropdown de fichas (pasó con "retiro-anticipado").
        const siteConfig = JSON.parse(getSiteConfig() || '{}');
        if (!siteConfig?.slug) {
          throw new Error('No se pudo determinar el sitio activo. Vuelve a iniciar sesión e inténtalo de nuevo.');
        }
        await makeAPIRequest(`/sites/${siteConfig.slug}/surveys`, 'post', {}, payload);
        toast({ title: 'Formulario creado', status: 'success', duration: 3000 });
      }
      setView('list');
      fetchSurveys();
    } catch (err) {
      setSaveError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Error al guardar');
    } finally {
      setSaveLoading(false);
    }
  };

  // Adjuntar al sitio activo
  const handleAttachToSite = async (survey) => {
    const siteConfig = JSON.parse(getSiteConfig() || '{}');
    if (!siteConfig?.id) {
      toast({ title: 'No hay sitio activo configurado', status: 'warning', duration: 3000 });
      return;
    }
    try {
      await makeAPIRequest(`/survey/${survey.id}/attachToSite`, 'post', {}, { site_id: siteConfig.id });
      toast({ title: `"${survey.name}" adjuntado al sitio activo`, status: 'success', duration: 3000 });
      fetchSurveys();
    } catch (err) {
      toast({ title: err?.response?.data?.message || err.message || 'Error al adjuntar', status: 'error', duration: 4000 });
    }
  };

  // Corrige de una vez todos los formularios detectados como huérfanos
  // (existen en la BD pero nunca se adjuntaron al sitio activo en `site_survey`,
  // por lo que son invisibles en el dropdown de fichas del perfil).
  const handleFixOrphans = async () => {
    if (!activeSite?.id || orphanedSurveys.length === 0) return;
    setFixingOrphans(true);
    let okCount = 0;
    let failCount = 0;
    for (const survey of orphanedSurveys) {
      try {
        await makeAPIRequest(`/survey/${survey.id}/attachToSite`, 'post', {}, { site_id: activeSite.id });
        okCount++;
      } catch {
        failCount++;
      }
    }
    toast({
      title: failCount === 0
        ? `${okCount} formulario${okCount !== 1 ? 's' : ''} adjuntado${okCount !== 1 ? 's' : ''} al sitio activo`
        : `${okCount} adjuntado${okCount !== 1 ? 's' : ''}, ${failCount} con error`,
      status: failCount === 0 ? 'success' : 'warning',
      duration: 4000,
    });
    setFixingOrphans(false);
    fetchSurveys();
  };

  // Duplicar
  const handleDuplicate = async (survey) => {
    try {
      await makeAPIRequest(`/survey/${survey.id}/duplicate`, 'post');
      toast({ title: `"${survey.name}" duplicado`, status: 'success', duration: 3000 });
      fetchSurveys();
    } catch (err) {
      toast({ title: err?.response?.data?.message || err.message || 'Error al duplicar', status: 'error', duration: 4000 });
    }
  };

  // Eliminar
  const handleDeleteOpen = (survey) => { setDeletingSurvey(survey); openDelete(); };
  const handleDelete = async () => {
    if (!deletingSurvey) return;
    setDeleteLoading(true);
    try {
      await makeAPIRequest(`/surveys/${deletingSurvey.id}`, 'delete');
      toast({ title: 'Formulario eliminado', status: 'success', duration: 3000 });
      closeDelete();
      fetchSurveys();
    } catch (err) {
      toast({ title: err?.response?.data?.message || err.message || 'Error al eliminar', status: 'error', duration: 4000 });
    } finally {
      setDeleteLoading(false);
    }
  };

  const filteredSurveys = surveys.filter((s) => {
    const matchSearch   = !search || s.name?.toLowerCase().includes(search.toLowerCase()) || s.slug?.toLowerCase().includes(search.toLowerCase());
    const matchCategory = !categoryFilter || s.category === categoryFilter;
    return matchSearch && matchCategory;
  });

  // ── Vista editor ────────────────────────────────────────────────────────────
  if (view === 'edit') {
    return (
      <Box>
        <Flex align="center" mb={5} gap={3}>
          <Button leftIcon={<ChevronLeftIcon />} size="sm" variant="ghost"
            onClick={() => { setView('list'); setEditingSurvey(null); }}
            isDisabled={saveLoading}>
            Volver a la lista
          </Button>
          <Text fontWeight="semibold" fontSize="md">
            {editingSurvey ? `Editar: ${editingSurvey.name}` : 'Nuevo formulario'}
          </Text>
        </Flex>

        {editorLoading ? (
          <Center py={20}><Spinner size="lg" color="blue.500" /></Center>
        ) : (
          <SurveyEditorForm
            initialForm={editorInitialForm}
            onSave={handleSave}
            onCancel={() => { setView('list'); setEditingSurvey(null); }}
            loading={saveLoading}
            saveError={saveError}
            categories={categories}
          />
        )}
      </Box>
    );
  }

  // ── Vista lista ─────────────────────────────────────────────────────────────
  return (
    <Box>
      <Flex mb={4} align="center" justify="space-between" flexWrap="wrap" gap={3}>
        <HStack flexWrap="wrap" spacing={3}>
          <Input placeholder="Buscar por nombre..." value={search}
            onChange={(e) => setSearch(e.target.value)} size="sm" maxW="240px" />
          <Select placeholder="Todas las categorías" value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)} size="sm" maxW="190px">
            {categories.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </Select>
          {(search || categoryFilter) && (
            <Button size="sm" variant="ghost"
              onClick={() => { setSearch(''); setCategoryFilter(''); }}>
              Limpiar
            </Button>
          )}
          <Text fontSize="sm" color="gray.500">
            {filteredSurveys.length} formulario{filteredSurveys.length !== 1 ? 's' : ''}
          </Text>
        </HStack>
        <Button colorScheme="blue" size="sm" onClick={handleNewSurvey}>+ Nuevo formulario</Button>
      </Flex>

      {orphanedSurveys.length > 0 && (
        <Alert status="warning" mb={4} borderRadius="md" flexWrap="wrap" rowGap={2}>
          <AlertIcon />
          <Box flex="1" fontSize="sm">
            {orphanedSurveys.length} formulario{orphanedSurveys.length !== 1 ? 's' : ''} no está{orphanedSurveys.length !== 1 ? 'n' : ''} adjunto{orphanedSurveys.length !== 1 ? 's' : ''} al sitio activo ({activeSite?.name || activeSite?.slug}) — no aparecerá{orphanedSurveys.length !== 1 ? 'n' : ''} en el dropdown de fichas hasta corregirlo.
          </Box>
          <Button size="sm" colorScheme="orange" onClick={handleFixOrphans} isLoading={fixingOrphans}>
            Adjuntar {orphanedSurveys.length === 1 ? 'al sitio activo' : 'todos al sitio activo'}
          </Button>
        </Alert>
      )}

      {listError && (
        <Alert status="error" mb={4} borderRadius="md"><AlertIcon />{listError}</Alert>
      )}

      {listLoading ? (
        <Center py={10}><Spinner size="lg" color="blue.500" /></Center>
      ) : (
        <Box overflowX="auto" borderRadius="md" boxShadow="sm" bg="white">
          <Table size="sm" variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Nombre</Th>
                <Th>Categoría</Th>
                <Th>Slug</Th>
                <Th isNumeric>Preguntas</Th>
                <Th>Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredSurveys.length === 0 ? (
                <Tr>
                  <Td colSpan={5} textAlign="center" py={8} color="gray.400">
                    {surveys.length === 0 ? 'No hay formularios creados' : 'Sin resultados para ese filtro'}
                  </Td>
                </Tr>
              ) : (
                filteredSurveys.map((s) => {
                  const isOrphan = orphanedSurveys.some((o) => o.id === s.id);
                  return (
                  <Tr key={s.id} _hover={{ bg: 'gray.50' }}>
                    <Td fontWeight="medium">
                      {s.name}
                      {isOrphan && (
                        <Badge ml={2} colorScheme="orange" fontSize="10px" title={`No adjunto a ${activeSite?.name || activeSite?.slug}`}>
                          Sin sitio
                        </Badge>
                      )}
                    </Td>
                    <Td>
                      <Badge colorScheme={categoryColorMap[s.category] || 'gray'} fontSize="xs">
                        {categories.find((c) => c.value === s.category)?.label || s.category || '—'}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontSize="xs" color="gray.500" fontFamily="mono">{s.slug}</Text>
                    </Td>
                    <Td isNumeric>
                      <Text fontSize="sm">{s.questions?.length ?? '—'}</Text>
                    </Td>
                    <Td>
                      <HStack spacing={1}>
                        <IconButton icon={<EditIcon />} size="xs" variant="ghost" colorScheme="blue"
                          aria-label="Editar" onClick={() => handleEditSurvey(s)} />
                        <IconButton icon={<CopyIcon />} size="xs" variant="ghost" colorScheme="teal"
                          aria-label="Duplicar" onClick={() => handleDuplicate(s)} />
                        <IconButton icon={<CheckIcon />} size="xs" variant="ghost" colorScheme={isOrphan ? 'orange' : 'green'}
                          aria-label="Adjuntar al sitio activo" title="Adjuntar al sitio activo"
                          onClick={() => handleAttachToSite(s)} />
                        <IconButton icon={<DeleteIcon />} size="xs" variant="ghost" colorScheme="red"
                          aria-label="Eliminar" onClick={() => handleDeleteOpen(s)} />
                      </HStack>
                    </Td>
                  </Tr>
                  );
                })
              )}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Modal Eliminar */}
      <Modal isOpen={isDeleteOpen} onClose={closeDelete} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Eliminar formulario</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              ¿Eliminar <strong>"{deletingSurvey?.name}"</strong>?
              Esta acción no se puede deshacer. Las respuestas existentes se conservarán.
            </Text>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button size="sm" variant="ghost" onClick={closeDelete}>Cancelar</Button>
              <Button size="sm" colorScheme="red" onClick={handleDelete} isLoading={deleteLoading}>
                Eliminar
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// ─── FormSyncPanel ────────────────────────────────────────────────────────────

const TARGET_OPTIONS = [
  { value: 'root',    label: 'root (campo raíz del customer)' },
  { value: 'billing', label: 'billing (facturación)' },
  { value: 'meta',    label: 'meta_data' },
];

const TYPE_OPTIONS = [
  { value: 'text',     label: 'Texto' },
  { value: 'phone',    label: 'Teléfono' },
  { value: 'email',    label: 'Email' },
  { value: 'date',     label: 'Fecha' },
  { value: 'number',   label: 'Número' },
  { value: 'textarea', label: 'Texto largo' },
];

const EMPTY_MAPPING = { formField: '', target: 'meta', key: '', label: '', type: 'text', syncAlso: [] };
const genSlug = () => `cfg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
const EMPTY_CONFIG  = { slug: '', category: 'matriculas', description: '', trigger: 'auto', column: 1, fieldMap: [] };

const FormSyncPanel = () => {
  const toast = useToast();
  const [syncCategories, setSyncCategories] = useState(DEFAULT_CATEGORIES_CONFIG);
  const [configs, setConfigs]   = useState({});

  useEffect(() => {
    makeAPIRequest('/survey-categories', 'get')
      .then((data) => setSyncCategories((Array.isArray(data) ? data : []).map(normalizeApiCategory)))
      .catch(() => {});
  }, []);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [editing, setEditing]   = useState(null); // slug en edición o 'new'
  const [form, setForm]         = useState(EMPTY_CONFIG);

  const borderColor  = useColorModeValue('gray.200', 'gray.600');
  const bgRow        = useColorModeValue('gray.50', 'gray.700');
  const bgEditBox    = useColorModeValue('blue.50', 'blue.900');
  const bgMappingRow = useColorModeValue('white', 'gray.800');

  const load = async () => {
    setLoading(true);
    try {
      const data = await loadFormSyncConfigs(true);
      setConfigs(data || {});
    } catch {
      toast({ title: 'Error al cargar configs', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew  = () => { setForm({ ...EMPTY_CONFIG, slug: genSlug() }); setEditing('new'); };
  const startEdit = (cfg) => { setForm({ ...cfg, fieldMap: cfg.fieldMap ? [...cfg.fieldMap] : [] }); setEditing(cfg.slug); };
  const cancel    = () => { setEditing(null); setForm(EMPTY_CONFIG); };

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const addMapping = () => setForm(f => ({ ...f, fieldMap: [...(f.fieldMap || []), { ...EMPTY_MAPPING }] }));

  const updateMapping = (idx, key, val) => setForm(f => {
    const fieldMap = [...(f.fieldMap || [])];
    fieldMap[idx]  = { ...fieldMap[idx], [key]: val };
    return { ...f, fieldMap };
  });

  const removeMapping = (idx) => setForm(f => ({
    ...f, fieldMap: f.fieldMap.filter((_, i) => i !== idx)
  }));

  const addSyncAlso = (idx) => setForm(f => {
    const fieldMap = [...f.fieldMap];
    fieldMap[idx]  = { ...fieldMap[idx], syncAlso: [...(fieldMap[idx].syncAlso || []), { target: 'meta', key: '' }] };
    return { ...f, fieldMap };
  });

  const updateSyncAlso = (mIdx, aIdx, key, val) => setForm(f => {
    const fieldMap    = [...f.fieldMap];
    const syncAlso    = [...(fieldMap[mIdx].syncAlso || [])];
    syncAlso[aIdx]    = { ...syncAlso[aIdx], [key]: val };
    fieldMap[mIdx]    = { ...fieldMap[mIdx], syncAlso };
    return { ...f, fieldMap };
  });

  const removeSyncAlso = (mIdx, aIdx) => setForm(f => {
    const fieldMap = [...f.fieldMap];
    fieldMap[mIdx] = { ...fieldMap[mIdx], syncAlso: fieldMap[mIdx].syncAlso.filter((_, i) => i !== aIdx) };
    return { ...f, fieldMap };
  });

  const save = async () => {
    if (!form.category) {
      toast({ title: 'La categoría es requerida', status: 'warning', duration: 2000, isClosable: true });
      return;
    }
    setSaving(true);
    try {
      if (editing === 'new') {
        await saveFormSyncConfig(form);
      } else {
        await updateFormSyncConfig(editing, form);
      }
      toast({ title: 'Configuración guardada', status: 'success', duration: 2000, isClosable: true });
      cancel();
      await load();
    } catch (err) {
      toast({ title: 'Error al guardar', description: err.message, status: 'error', duration: 3000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`¿Eliminar configuración "${slug}"?`)) return;
    try {
      await deleteFormSyncConfig(slug);
      toast({ title: `"${slug}" eliminado`, status: 'success', duration: 2000, isClosable: true });
      await load();
    } catch (err) {
      toast({ title: 'Error al eliminar', description: err.message, status: 'error', duration: 3000, isClosable: true });
    }
  };

  if (loading) return <Center py={10}><Spinner /></Center>;

  return (
    <VStack align="stretch" spacing={6}>
      <HStack justify="space-between">
        <VStack align="flex-start" spacing={0}>
          <Heading size="sm">Sync Formularios → Perfil</Heading>
          <Text fontSize="xs" color="gray.500">
            Define qué campos de cada formulario actualizan el perfil del alumno al completarse.
          </Text>
        </VStack>
        {!editing && (
          <Button size="sm" colorScheme="blue" onClick={startNew}>+ Nueva configuración</Button>
        )}
      </HStack>

      {/* Lista de configs existentes */}
      {!editing && (
        <VStack align="stretch" spacing={3}>
          {Object.values(configs).length === 0 && (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              Sin configuraciones. Crea la primera para activar el sync automático.
            </Alert>
          )}
          {Object.values(configs).map(cfg => (
            <Box key={cfg.slug} p={4} border="1px solid" borderColor={borderColor}
              borderRadius="md" bg={bgRow}>
              <HStack justify="space-between">
                <VStack align="flex-start" spacing={1}>
                  <HStack>
                    <Badge colorScheme="blue">{cfg.slug}</Badge>
                    <Badge colorScheme="purple">{cfg.category}</Badge>
                    <Badge colorScheme={cfg.trigger === 'auto' ? 'green' : 'orange'}>{cfg.trigger}</Badge>
                  </HStack>
                  <Text fontSize="sm" color="gray.500">{cfg.description}</Text>
                  <Text fontSize="xs" color="gray.400">{(cfg.fieldMap || []).length} mapeos configurados</Text>
                </VStack>
                <HStack>
                  <IconButton size="sm" icon={<EditIcon />} variant="ghost"
                    onClick={() => startEdit(cfg)} aria-label="Editar" />
                  <IconButton size="sm" icon={<DeleteIcon />} variant="ghost" colorScheme="red"
                    onClick={() => remove(cfg.slug)} aria-label="Eliminar" />
                </HStack>
              </HStack>
            </Box>
          ))}
        </VStack>
      )}

      {/* Formulario de edición */}
      {editing && (
        <Box p={5} border="1px solid" borderColor="blue.200" borderRadius="md"
          bg={bgEditBox}>
          <Heading size="sm" mb={4}>{editing === 'new' ? 'Nueva configuración' : `Editando: ${editing}`}</Heading>

          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} mb={4}>
            <FormControl>
              <FormLabel fontSize="sm">Categoría *</FormLabel>
              <Select size="sm" value={form.category} onChange={e => setField('category', e.target.value)}>
                {syncCategories.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </Select>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Cuando se guarda una ficha de esta categoría, se sincronizan todos los configs que la tengan.
              </Text>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Título del card en Antecedentes</FormLabel>
              <Input size="sm" value={form.description} onChange={e => setField('description', e.target.value)}
                placeholder="ej: Apoderado principal, Antecedentes médicos" />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Trigger</FormLabel>
              <Select size="sm" value={form.trigger} onChange={e => setField('trigger', e.target.value)}>
                <option value="auto">auto (al enviar el formulario)</option>
                <option value="manual">manual (requiere acción del admin)</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Columna en Antecedentes</FormLabel>
              <Select size="sm" value={form.column || 1} onChange={e => setField('column', Number(e.target.value))}>
                <option value={1}>Izquierda</option>
                <option value={2}>Derecha</option>
              </Select>
            </FormControl>
          </SimpleGrid>

          <Divider mb={4} />

          {/* Mapeos de campos */}
          <HStack justify="space-between" mb={3}>
            <Text fontSize="sm" fontWeight="bold">Mapeos de campos</Text>
            <Button size="xs" onClick={addMapping}>+ Agregar mapeo</Button>
          </HStack>

          <VStack align="stretch" spacing={3}>
            {(form.fieldMap || []).map((mapping, mIdx) => (
              <Box key={mIdx} p={3} border="1px solid" borderColor={borderColor}
                borderRadius="md" bg={bgMappingRow}>
                <SimpleGrid columns={{ base: 1, md: 3 }} gap={2} mb={2}>
                  <FormControl>
                    <FormLabel fontSize="xs">ID de la pregunta</FormLabel>
                    <Input size="xs" value={mapping.formField}
                      onChange={e => updateMapping(mIdx, 'formField', e.target.value)}
                      placeholder="ej: 54, 55, 59" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs">Destino</FormLabel>
                    <Select size="xs" value={mapping.target}
                      onChange={e => updateMapping(mIdx, 'target', e.target.value)}>
                      {TARGET_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs">Clave meta_data (key)</FormLabel>
                    <Input size="xs" value={mapping.key}
                      onChange={e => updateMapping(mIdx, 'key', e.target.value)}
                      placeholder="ej: apoderado_nombres, grupo_sanguineo" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs">Etiqueta para mostrar</FormLabel>
                    <Input size="xs" value={mapping.label || ''}
                      onChange={e => updateMapping(mIdx, 'label', e.target.value)}
                      placeholder="ej: Grupo sanguíneo, Nombres" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs">Tipo de campo</FormLabel>
                    <Select size="xs" value={mapping.type || 'text'}
                      onChange={e => updateMapping(mIdx, 'type', e.target.value)}>
                      {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Select>
                  </FormControl>
                </SimpleGrid>

                {/* syncAlso */}
                {(mapping.syncAlso || []).map((also, aIdx) => (
                  <HStack key={aIdx} mt={1} pl={4} spacing={2}>
                    <Text fontSize="xs" color="gray.500">↳ también:</Text>
                    <Select size="xs" w="160px" value={also.target}
                      onChange={e => updateSyncAlso(mIdx, aIdx, 'target', e.target.value)}>
                      {TARGET_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Select>
                    <Input size="xs" w="160px" value={also.key}
                      onChange={e => updateSyncAlso(mIdx, aIdx, 'key', e.target.value)}
                      placeholder="clave" />
                    <IconButton size="xs" icon={<DeleteIcon />} variant="ghost" colorScheme="red"
                      onClick={() => removeSyncAlso(mIdx, aIdx)} aria-label="Quitar" />
                  </HStack>
                ))}

                <HStack mt={2} justify="space-between">
                  <Button size="xs" variant="ghost" onClick={() => addSyncAlso(mIdx)}>
                    + sincronizar también en...
                  </Button>
                  <IconButton size="xs" icon={<DeleteIcon />} colorScheme="red" variant="ghost"
                    onClick={() => removeMapping(mIdx)} aria-label="Eliminar mapeo" />
                </HStack>
              </Box>
            ))}
          </VStack>

          <HStack mt={5} justify="flex-end">
            <Button size="sm" variant="ghost" onClick={cancel}>Cancelar</Button>
            <Button size="sm" colorScheme="blue" onClick={save} isLoading={saving}>
              Guardar configuración
            </Button>
          </HStack>
        </Box>
      )}
    </VStack>
  );
};

// ─── Panel: Categorías ───────────────────────────────────────────────────────

const CategoriasPanel = ({ roles }) => {
  const toast = useToast();
  const [categories, setCategories]   = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const { isOpen, onOpen, onClose }   = useDisclosure();
  const [editingCat, setEditingCat]   = useState(null);
  const [catForm, setCatForm]         = useState(emptyCategory());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { isOpen: isDelOpen, onOpen: onDelOpen, onClose: onDelClose } = useDisclosure();

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgTable     = useColorModeValue('white', 'gray.800');

  const fetchCategories = useCallback(async () => {
    setListLoading(true);
    try {
      const data = await makeAPIRequest('/survey-categories', 'get');
      setCategories((Array.isArray(data) ? data : []).map(normalizeApiCategory));
    } catch (err) {
      toast({ title: 'Error al cargar categorías', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const handleNew = () => {
    setEditingCat(null);
    setCatForm(emptyCategory());
    onOpen();
  };

  const handleEdit = (cat) => {
    setEditingCat(cat);
    setCatForm(JSON.parse(JSON.stringify(cat)));
    onOpen();
  };

  const handleDeleteOpen = (cat) => { setDeleteTarget(cat); onDelOpen(); };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await makeAPIRequest(`/survey-categories/${deleteTarget.id}`, 'delete');
      clearCategoriesCache();
      toast({ title: `Categoría "${deleteTarget.label}" eliminada`, status: 'success', duration: 2000 });
      onDelClose();
      fetchCategories();
    } catch (err) {
      toast({ title: 'Error al eliminar', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    if (!catForm.label.trim() || !catForm.value.trim()) {
      toast({ title: 'Nombre y slug son requeridos', status: 'warning', duration: 2000 });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        slug:        catForm.value,
        label:       catForm.label,
        color:       catForm.color,
        role_access: catForm.roleAccess,
        sort_order:  catForm.sort_order ?? 0,
      };
      if (editingCat?.id) {
        await makeAPIRequest(`/survey-categories/${editingCat.id}`, 'put', {}, payload);
        toast({ title: 'Categoría actualizada', status: 'success', duration: 2000 });
      } else {
        await makeAPIRequest('/survey-categories', 'post', {}, payload);
        toast({ title: 'Categoría creada', status: 'success', duration: 2000 });
      }
      clearCategoriesCache();
      onClose();
      fetchCategories();
    } catch (err) {
      const msg = err?.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join(' ')
        : err?.response?.data?.message || err.message;
      toast({ title: 'Error al guardar', description: msg, status: 'error', duration: 4000 });
    } finally {
      setSaving(false);
    }
  };

  const setRoleAccess = (role, field, value) =>
    setCatForm((f) => ({
      ...f,
      roleAccess: {
        ...f.roleAccess,
        [role]: { ...f.roleAccess?.[role], [field]: value },
      },
    }));

  return (
    <Box>
      <Flex mb={4} align="flex-start" justify="space-between" flexWrap="wrap" gap={3}>
        <Box>
          <Text fontWeight="semibold" fontSize="md">Gestión de Categorías</Text>
          <Text fontSize="sm" color="gray.500" mt={1}>
            Define las categorías de formularios y qué roles pueden verlas y completarlas.
            Los cambios aplican al gestor de formularios y al acceso desde el perfil del alumno.
          </Text>
        </Box>
        <Button colorScheme="blue" size="sm" onClick={handleNew}>+ Nueva categoría</Button>
      </Flex>

      {listLoading ? (
        <Center py={10}><Spinner size="lg" color="blue.500" /></Center>
      ) : (
      <Box overflowX="auto" borderRadius="md" boxShadow="sm" bg={bgTable} border="1px solid" borderColor={borderColor}>
        <Table size="sm" variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th>Categoría</Th>
              <Th>Slug</Th>
              <Th>Puede ver</Th>
              <Th>Puede completar</Th>
              <Th>Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {categories.map((cat) => (
              <Tr key={cat.value} _hover={{ bg: 'gray.50' }}>
                <Td>
                  <Badge colorScheme={cat.color || 'gray'}>{cat.label}</Badge>
                </Td>
                <Td>
                  <Text fontSize="xs" fontFamily="mono" color="gray.500">{cat.value}</Text>
                </Td>
                <Td>
                  <HStack spacing={1} flexWrap="wrap">
                    {(roles || []).map((r) =>
                      cat.roleAccess?.[r.value]?.view ? (
                        <Badge key={r.value} colorScheme={r.color} fontSize="2xs" variant="subtle">
                          {r.label}
                        </Badge>
                      ) : null
                    )}
                  </HStack>
                </Td>
                <Td>
                  <HStack spacing={1} flexWrap="wrap">
                    {(roles || []).map((r) =>
                      cat.roleAccess?.[r.value]?.fill ? (
                        <Badge key={r.value} colorScheme={r.color} fontSize="2xs" variant="subtle">
                          {r.label}
                        </Badge>
                      ) : null
                    )}
                  </HStack>
                </Td>
                <Td>
                  <HStack spacing={1}>
                    <IconButton icon={<EditIcon />} size="xs" variant="ghost" colorScheme="blue"
                      aria-label="Editar categoría" onClick={() => handleEdit(cat)} />
                    <IconButton icon={<DeleteIcon />} size="xs" variant="ghost" colorScheme="red"
                      aria-label="Eliminar categoría" onClick={() => handleDeleteOpen(cat)} />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      )}

      {/* Modal crear / editar */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingCat ? `Editar: ${editingCat.label}` : 'Nueva categoría'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <HStack mb={4} align="flex-start" spacing={4}>
              <FormControl isRequired flex={2}>
                <FormLabel fontSize="sm">Nombre</FormLabel>
                <Input size="sm" value={catForm.label}
                  onChange={(e) => setCatForm((f) => ({ ...f, label: e.target.value }))}
                  placeholder="Ej: Matrículas, Salud, Becas…" />
              </FormControl>
              <FormControl isRequired flex={1}>
                <FormLabel fontSize="sm">Slug (identificador)</FormLabel>
                <Input size="sm" value={catForm.value}
                  onChange={(e) => setCatForm((f) => ({ ...f, value: e.target.value.toLowerCase().replace(/\s+/g, '-') }))}
                  isReadOnly={!!editingCat}
                  bg={editingCat ? 'gray.50' : undefined}
                  placeholder="ej: matriculas" />
              </FormControl>
              <FormControl flex={1}>
                <FormLabel fontSize="sm">Color</FormLabel>
                <Select size="sm" value={catForm.color || 'gray'}
                  onChange={(e) => setCatForm((f) => ({ ...f, color: e.target.value }))}>
                  {COLOR_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </Select>
              </FormControl>
            </HStack>

            {catForm.color && (
              <Flex mb={4} align="center" gap={2}>
                <Text fontSize="xs" color="gray.500">Vista previa:</Text>
                <Badge colorScheme={catForm.color}>{catForm.label || 'Categoría'}</Badge>
              </Flex>
            )}

            <Divider mb={4} />

            <Text fontWeight="semibold" fontSize="sm" mb={3}>Acceso por rol</Text>
            <Box overflowX="auto" borderRadius="md" border="1px solid" borderColor={borderColor}>
              <Table size="sm" variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Rol</Th>
                    <Th textAlign="center">Puede ver formularios</Th>
                    <Th textAlign="center">Puede completar formularios</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(roles || []).map((role) => (
                    <Tr key={role.value}>
                      <Td>
                        <Badge colorScheme={role.color}>{role.label}</Badge>
                      </Td>
                      <Td textAlign="center">
                        <Checkbox
                          isChecked={catForm.roleAccess?.[role.value]?.view ?? false}
                          onChange={(e) => setRoleAccess(role.value, 'view', e.target.checked)}
                          colorScheme={role.color}
                        />
                      </Td>
                      <Td textAlign="center">
                        <Checkbox
                          isChecked={catForm.roleAccess?.[role.value]?.fill ?? false}
                          onChange={(e) => setRoleAccess(role.value, 'fill', e.target.checked)}
                          colorScheme={role.color}
                          isDisabled={!catForm.roleAccess?.[role.value]?.view}
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
            <Text fontSize="xs" color="gray.400" mt={2}>
              Un rol que no puede ver la categoría tampoco puede completar formularios de ella.
            </Text>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button size="sm" variant="ghost" onClick={onClose}>Cancelar</Button>
              <Button size="sm" colorScheme="blue"
                isDisabled={!catForm.label.trim() || !catForm.value.trim()}
                isLoading={saving}
                onClick={handleSave}>
                {editingCat ? 'Guardar cambios' : 'Crear categoría'}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal eliminar */}
      <Modal isOpen={isDelOpen} onClose={onDelClose} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Eliminar categoría</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              ¿Eliminar la categoría <strong>"{deleteTarget?.label}"</strong>?
              Los formularios existentes con esta categoría no serán afectados,
              pero dejarán de aparecer en los filtros.
            </Text>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button size="sm" variant="ghost" onClick={onDelClose}>Cancelar</Button>
              <Button size="sm" colorScheme="red" onClick={handleDelete} isLoading={deleting}>Eliminar</Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// ─── Panel: Roles ────────────────────────────────────────────────────────────

const emptyRoleForm = () => ({ label: '', slug: '', color: 'blue', permissions: [] });

const RolesPanel = ({ roles, onRolesChange }) => {
  const toast = useToast();
  const [catalog, setCatalog]         = useState([]);
  const [saving, setSaving]           = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const { isOpen, onOpen, onClose }   = useDisclosure();
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm]       = useState(emptyRoleForm());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { isOpen: isDelOpen, onOpen: onDelOpen, onClose: onDelClose } = useDisclosure();

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgTable     = useColorModeValue('white', 'gray.800');

  useEffect(() => {
    getPermissionsCatalog()
      .then((data) => setCatalog(Array.isArray(data) ? data : []))
      .catch((err) => toast({ title: 'Error al cargar permisos', description: err.message, status: 'error', duration: 4000 }));
  }, [toast]);

  const handleNew = () => {
    setEditingRole(null);
    setRoleForm(emptyRoleForm());
    onOpen();
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setRoleForm({
      label:       role.label,
      slug:        role.value,
      color:       role.color || 'gray',
      permissions: role.permissions || [],
    });
    onOpen();
  };

  const togglePermission = (key) =>
    setRoleForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter((p) => p !== key)
        : [...f.permissions, key],
    }));

  const handleSave = async () => {
    if (!roleForm.label.trim() || !roleForm.slug.trim()) {
      toast({ title: 'Nombre y slug son requeridos', status: 'warning', duration: 2000 });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        slug:        roleForm.slug,
        label:       roleForm.label,
        color:       roleForm.color,
        permissions: roleForm.permissions,
      };
      if (editingRole?.id) {
        await updateRole(editingRole.id, payload);
        toast({ title: 'Rol actualizado', status: 'success', duration: 2000 });
      } else {
        await createRole(payload);
        toast({ title: 'Rol creado', status: 'success', duration: 2000 });
      }
      onClose();
      onRolesChange();
    } catch (err) {
      const msg = err?.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join(' ')
        : err?.response?.data?.message || err.message;
      toast({ title: 'Error al guardar', description: msg, status: 'error', duration: 4000 });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOpen = (role) => { setDeleteTarget(role); onDelOpen(); };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteRole(deleteTarget.id);
      toast({ title: `Rol "${deleteTarget.label}" eliminado`, status: 'success', duration: 2000 });
      onDelClose();
      onRolesChange();
    } catch (err) {
      toast({
        title: 'Error al eliminar',
        description: err?.response?.data?.message || err.message,
        status: 'error', duration: 4000,
      });
    } finally {
      setDeleting(false);
    }
  };

  // Agrupa el catálogo de permisos por área (todos "Sistema" por ahora)
  const groupedCatalog = catalog.reduce((acc, item) => {
    (acc[item.group] = acc[item.group] || []).push(item);
    return acc;
  }, {});

  return (
    <Box>
      <Flex mb={4} align="flex-start" justify="space-between" flexWrap="wrap" gap={3}>
        <Box>
          <Text fontWeight="semibold" fontSize="md">Gestión de Roles</Text>
          <Text fontSize="sm" color="gray.500" mt={1}>
            Crea roles adicionales o edita el nombre/color de los roles existentes.
            Los permisos definen qué puede administrar cada rol dentro de Sistema.
          </Text>
        </Box>
        <Button colorScheme="blue" size="sm" onClick={handleNew}>+ Nuevo rol</Button>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="sm" bg={bgTable} border="1px solid" borderColor={borderColor}>
        <Table size="sm" variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th>Rol</Th>
              <Th>Slug</Th>
              <Th>Permisos</Th>
              <Th>Tipo</Th>
              <Th>Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {roles.map((role) => (
              <Tr key={role.value} _hover={{ bg: 'gray.50' }}>
                <Td>
                  <Badge colorScheme={role.color}>{role.label}</Badge>
                </Td>
                <Td>
                  <Text fontSize="xs" fontFamily="mono" color="gray.500">{role.value}</Text>
                </Td>
                <Td>
                  {role.permissions?.length ? (
                    <Text fontSize="xs" color="gray.500">{role.permissions.length} permiso(s)</Text>
                  ) : (
                    <Text fontSize="xs" color="gray.400">Sin permisos</Text>
                  )}
                </Td>
                <Td>
                  {role.isSystem ? (
                    <Badge colorScheme="gray" fontSize="2xs" variant="subtle">Sistema</Badge>
                  ) : (
                    <Badge colorScheme="purple" fontSize="2xs" variant="subtle">Personalizado</Badge>
                  )}
                </Td>
                <Td>
                  <HStack spacing={1}>
                    <IconButton icon={<EditIcon />} size="xs" variant="ghost" colorScheme="blue"
                      aria-label="Editar rol" onClick={() => handleEdit(role)} />
                    <IconButton icon={<DeleteIcon />} size="xs" variant="ghost" colorScheme="red"
                      aria-label="Eliminar rol" isDisabled={role.isSystem}
                      title={role.isSystem ? 'Los roles del sistema no se pueden eliminar' : undefined}
                      onClick={() => handleDeleteOpen(role)} />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Modal crear / editar */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingRole ? `Editar: ${editingRole.label}` : 'Nuevo rol'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <HStack mb={4} align="flex-start" spacing={4}>
              <FormControl isRequired flex={2}>
                <FormLabel fontSize="sm">Nombre</FormLabel>
                <Input size="sm" value={roleForm.label}
                  onChange={(e) => setRoleForm((f) => ({ ...f, label: e.target.value }))}
                  placeholder="Ej: Coordinador, Recepción…" />
              </FormControl>
              <FormControl isRequired flex={1}>
                <FormLabel fontSize="sm">Slug (identificador)</FormLabel>
                <Input size="sm" value={roleForm.slug}
                  onChange={(e) => setRoleForm((f) => ({ ...f, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))}
                  isReadOnly={!!editingRole}
                  bg={editingRole ? 'gray.50' : undefined}
                  placeholder="ej: coordinador" />
              </FormControl>
              <FormControl flex={1}>
                <FormLabel fontSize="sm">Color</FormLabel>
                <Select size="sm" value={roleForm.color || 'gray'}
                  onChange={(e) => setRoleForm((f) => ({ ...f, color: e.target.value }))}>
                  {COLOR_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </Select>
              </FormControl>
            </HStack>

            {roleForm.color && (
              <Flex mb={4} align="center" gap={2}>
                <Text fontSize="xs" color="gray.500">Vista previa:</Text>
                <Badge colorScheme={roleForm.color}>{roleForm.label || 'Rol'}</Badge>
              </Flex>
            )}

            <Divider mb={4} />

            <Text fontWeight="semibold" fontSize="sm" mb={3}>Permisos</Text>
            {editingRole?.isSystem && (
              <Alert status="info" fontSize="xs" borderRadius="md" mb={3}>
                <AlertIcon />
                Los permisos de los roles del sistema (administrador/profesor/usuario) no son editables desde aquí.
              </Alert>
            )}
            {Object.entries(groupedCatalog).map(([group, items]) => (
              <Box key={group} mb={3}>
                <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2} textTransform="uppercase">
                  {group}
                </Text>
                <VStack align="stretch" spacing={2}>
                  {items.map((item) => (
                    <Checkbox key={item.key}
                      isChecked={roleForm.permissions.includes(item.key)}
                      onChange={() => togglePermission(item.key)}
                      isDisabled={!!editingRole?.isSystem}
                      colorScheme={roleForm.color}>
                      <Text fontSize="sm">{item.label}</Text>
                    </Checkbox>
                  ))}
                </VStack>
              </Box>
            ))}
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button size="sm" variant="ghost" onClick={onClose}>Cancelar</Button>
              <Button size="sm" colorScheme="blue"
                isDisabled={!roleForm.label.trim() || !roleForm.slug.trim()}
                isLoading={saving}
                onClick={handleSave}>
                {editingRole ? 'Guardar cambios' : 'Crear rol'}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal eliminar */}
      <Modal isOpen={isDelOpen} onClose={onDelClose} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Eliminar rol</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              ¿Eliminar el rol <strong>"{deleteTarget?.label}"</strong>?
              Esta acción no se puede deshacer. Si hay usuarios con este rol asignado,
              no se podrá eliminar hasta reasignarlos.
            </Text>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button size="sm" variant="ghost" onClick={onDelClose}>Cancelar</Button>
              <Button size="sm" colorScheme="red" onClick={handleDelete} isLoading={deleting}>Eliminar</Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// ─── Vista principal ──────────────────────────────────────────────────────────

const SistemaUsuarios = () => {
  const toast = useToast();

  // Catálogo de roles — cargado una vez y compartido entre Usuarios, Categorías y Roles
  const [roles, setRoles] = useState(FALLBACK_ROLES);
  const visibleRoles = useMemo(() => roles.filter((r) => r.value !== 'user'), [roles]);

  const fetchRoles = useCallback(async () => {
    try {
      const data = await getRoles();
      setRoles((Array.isArray(data) ? data : []).map(normalizeApiRole));
    } catch (err) {
      toast({ title: 'Error al cargar roles', description: err.message, status: 'error', duration: 4000 });
    }
  }, [toast]);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  // Tabla usuarios
  const [users, setUsers]               = useState([]);
  const [total, setTotal]               = useState(0);
  const [pages, setPages]               = useState(1);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [tableError, setTableError]     = useState('');

  // Filtros usuarios
  const [search, setSearch]         = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage]             = useState(1);
  const PER_PAGE = 20;

  // Modal crear usuario
  const { isOpen: isCreateOpen, onOpen: openCreate, onClose: closeCreate } = useDisclosure();
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError]     = useState('');

  // Modal editar usuario
  const { isOpen: isEditOpen, onOpen: openEdit, onClose: closeEdit } = useDisclosure();
  const [editUser, setEditUser]       = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError]     = useState('');

  // Modal eliminar usuario
  const { isOpen: isDeleteOpen, onOpen: openDelete, onClose: closeDelete } = useDisclosure();
  const [deleteUser, setDeleteUser]       = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [assigningUserId, setAssigningUserId] = useState(null);
  const [statusLoadingId, setStatusLoadingId] = useState(null);

  // ── Cargar usuarios ─────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    setTableError('');
    try {
      const result = await getAdminUsers({ search, role: roleFilter, page, per_page: PER_PAGE });
      setUsers(result.data || []);
      setTotal(result.total || 0);
      setPages(result.pages || 1);
    } catch (err) {
      if (err?.response?.status === 401) {
        window.location.href = '/auth/signin';
      } else {
        setTableError(err?.response?.data?.message || err.message || 'Error al cargar usuarios');
      }
    } finally {
      setLoadingUsers(false);
    }
  }, [search, roleFilter, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSearch      = (e) => { setSearch(e.target.value); setPage(1); };
  const handleRoleFilter  = (e) => { setRoleFilter(e.target.value); setPage(1); };

  const handleCreate = async (form) => {
    setCreateError('');
    setCreateLoading(true);
    try {
      const siteConfig = JSON.parse(getSiteConfig() || '{}');
      await createAdminUser({ ...form, site_id: siteConfig?.id });
      toast({ title: 'Usuario creado correctamente', status: 'success', duration: 3000 });
      closeCreate();
      fetchUsers();
    } catch (err) {
      setCreateError(err?.response?.data?.message || err.message || 'Error al crear usuario');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditOpen = (user) => { setEditUser(user); setEditError(''); openEdit(); };

  const handleUpdate = async (form) => {
    if (!editUser) return;
    setEditError('');
    setEditLoading(true);
    try {
      await updateAdminUser(editUser.username, form);
      toast({ title: 'Usuario actualizado correctamente', status: 'success', duration: 3000 });
      closeEdit();
      fetchUsers();
    } catch (err) {
      setEditError(err?.response?.data?.message || err.message || 'Error al actualizar usuario');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteOpen = (user) => { setDeleteUser(user); openDelete(); };

  const handleDelete = async () => {
    if (!deleteUser) return;
    setDeleteLoading(true);
    try {
      await deleteAdminUser(deleteUser.username);
      toast({ title: 'Usuario eliminado', status: 'success', duration: 3000 });
      closeDelete();
      fetchUsers();
    } catch (err) {
      toast({ title: err?.response?.data?.message || err.message || 'Error al eliminar', status: 'error', duration: 4000 });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleAssignSite = async (user) => {
    const siteConfig = JSON.parse(getSiteConfig() || '{}');
    const siteId = siteConfig?.id;
    if (!siteId) {
      toast({ title: 'No hay sitio activo configurado', status: 'warning', duration: 3000 });
      return;
    }
    setAssigningUserId(user.id);
    try {
      await assignUserToSite(user.username, siteId);
      toast({
        title: `${user.username} asignado a "${siteConfig.name}"`,
        description: 'El usuario ya puede iniciar sesión.',
        status: 'success', duration: 3000,
      });
      fetchUsers();
    } catch (err) {
      toast({ title: err?.response?.data?.message || err.message || 'Error al asignar sitio', status: 'error', duration: 4000 });
    } finally {
      setAssigningUserId(null);
    }
  };

  const handleSetStatus = async (user, newStatus) => {
    setStatusLoadingId(user.id);
    try {
      await setUserStatus(user.username, newStatus);
      toast({
        title: newStatus === 'suspended'
          ? `${user.username} suspendido`
          : `${user.username} reactivado`,
        status: newStatus === 'suspended' ? 'warning' : 'success',
        duration: 3000,
      });
      fetchUsers();
    } catch (err) {
      toast({ title: err?.response?.data?.message || err.message || 'Error al cambiar estado', status: 'error', duration: 4000 });
    } finally {
      setStatusLoadingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CL');
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box px={{ base: 4, md: 6 }}>
      <Heading size="md" mb={5}>Sistema</Heading>

      <Tabs variant="enclosed" colorScheme="blue">
        <TabList>
          <Tab>Usuarios</Tab>
          <Tab>Roles</Tab>
          <Tab>Formularios</Tab>
          <Tab>Categorías</Tab>
          <Tab>Sitio activo</Tab>
          <Tab>Sync Formularios</Tab>
        </TabList>

        <TabPanels>
          {/* ── Tab: Usuarios ────────────────────────────────────────────── */}
          <TabPanel px={0} pt={4}>
            <Flex mb={4} align="center" justify="space-between" flexWrap="wrap" gap={3}>
              <HStack flexWrap="wrap" spacing={3}>
                <Input
                  placeholder="Buscar por nombre, email..."
                  value={search} onChange={handleSearch} size="sm" maxW="280px"
                />
                <Select placeholder="Todos los roles" value={roleFilter}
                  onChange={handleRoleFilter} size="sm" maxW="200px">
                  {visibleRoles.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </Select>
                <Button size="sm" variant="ghost"
                  onClick={() => { setSearch(''); setRoleFilter(''); setPage(1); }}>
                  Limpiar
                </Button>
                <Text fontSize="sm" color="gray.500">
                  {total} usuario{total !== 1 ? 's' : ''}
                </Text>
              </HStack>
              <Button colorScheme="blue" size="sm" onClick={openCreate}>+ Crear usuario</Button>
            </Flex>

            {tableError && (
              <Alert status="error" mb={4} borderRadius="md">
                <AlertIcon />{tableError}
              </Alert>
            )}

            {loadingUsers ? (
              <Center py={10}><Spinner size="lg" color="blue.500" /></Center>
            ) : (
              <Box overflowX="auto" borderRadius="md" boxShadow="sm" bg="white">
                <Table size="sm" variant="simple">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>ID</Th><Th>Nombre</Th><Th>Email</Th>
                      <Th>Rol</Th><Th>Estado</Th><Th>Registro</Th><Th>Acciones</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {users.length === 0 ? (
                      <Tr>
                        <Td colSpan={7} textAlign="center" py={6} color="gray.400">
                          No se encontraron usuarios
                        </Td>
                      </Tr>
                    ) : (
                      users.map((u) => (
                        <Tr key={u.id} _hover={{ bg: 'gray.50' }}
                          bg={u.status === 'suspended' ? 'red.50' : (!u.sites?.length ? 'orange.50' : undefined)}
                          opacity={u.status === 'suspended' ? 0.8 : 1}>
                          <Td>{u.id}</Td>
                          <Td fontWeight="medium">
                            {`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username}
                            {!u.sites?.length && (
                              <Badge colorScheme="orange" ml={2} fontSize="2xs">sin sitio</Badge>
                            )}
                          </Td>
                          <Td>{u.email}</Td>
                          <Td>
                            <Badge colorScheme={getRoleColor(roles, u.role)} fontSize="xs">
                              {getRoleLabel(roles, u.role)}
                            </Badge>
                          </Td>
                          <Td>
                            <Badge colorScheme={STATUS_COLORS[u.status] || 'gray'} fontSize="xs">
                              {STATUS_LABELS[u.status] || u.status || 'Activo'}
                            </Badge>
                          </Td>
                          <Td>{formatDate(u.created_at)}</Td>
                          <Td>
                            <HStack spacing={1}>
                              {!u.sites?.length && (
                                <IconButton icon={<AddIcon />} size="xs" colorScheme="orange"
                                  aria-label="Asignar al sitio activo"
                                  title="Asignar al sitio activo"
                                  isLoading={assigningUserId === u.id}
                                  onClick={() => handleAssignSite(u)} />
                              )}
                              {u.status === 'suspended' ? (
                                <IconButton
                                  icon={<CheckIcon />} size="xs" colorScheme="green" variant="ghost"
                                  aria-label="Reactivar usuario" title="Reactivar usuario"
                                  isLoading={statusLoadingId === u.id}
                                  onClick={() => handleSetStatus(u, 'active')} />
                              ) : (
                                <IconButton
                                  icon={<ArrowDownIcon />} size="xs" colorScheme="orange" variant="ghost"
                                  aria-label="Suspender usuario" title="Suspender usuario"
                                  isLoading={statusLoadingId === u.id}
                                  onClick={() => handleSetStatus(u, 'suspended')} />
                              )}
                              <IconButton icon={<EditIcon />} size="xs" variant="ghost"
                                colorScheme="blue" aria-label="Editar usuario"
                                onClick={() => handleEditOpen(u)} />
                              <IconButton icon={<DeleteIcon />} size="xs" variant="ghost"
                                colorScheme="red" aria-label="Eliminar usuario"
                                onClick={() => handleDeleteOpen(u)} />
                            </HStack>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            )}

            {pages > 1 && (
              <HStack mt={4} justify="center" spacing={2}>
                <Button size="xs" onClick={() => setPage((p) => Math.max(1, p - 1))}
                  isDisabled={page === 1}>‹ Anterior</Button>
                <Text fontSize="sm">Página {page} de {pages}</Text>
                <Button size="xs" onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  isDisabled={page === pages}>Siguiente ›</Button>
              </HStack>
            )}
          </TabPanel>

          {/* ── Tab: Roles ───────────────────────────────────────────────── */}
          <TabPanel px={0} pt={4}>
            <RolesPanel roles={roles} onRolesChange={fetchRoles} />
          </TabPanel>

          {/* ── Tab: Formularios ─────────────────────────────────────────── */}
          <TabPanel px={0} pt={4}>
            <FormulariosPanel />
          </TabPanel>

          {/* ── Tab: Categorías ──────────────────────────────────────────── */}
          <TabPanel px={0} pt={4}>
            <CategoriasPanel roles={visibleRoles} />
          </TabPanel>

          {/* ── Tab: Sitio activo ─────────────────────────────────────────── */}
          <TabPanel px={0} pt={4}>
            <SiteConfigPanel />
          </TabPanel>

          {/* ── Tab: Sync Formularios ─────────────────────────────────────── */}
          <TabPanel px={0} pt={4}>
            <FormSyncPanel />
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Modal Crear usuario */}
      <Modal isOpen={isCreateOpen} onClose={() => { closeCreate(); setCreateError(''); }} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Crear nuevo usuario</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <UserForm onSubmit={handleCreate} loading={createLoading} error={createError} isEdit={false} roles={roles} />
          </ModalBody>
          <ModalFooter />
        </ModalContent>
      </Modal>

      {/* Modal Editar usuario */}
      <Modal isOpen={isEditOpen} onClose={() => { closeEdit(); setEditError(''); }} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Editar usuario</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <UserForm initialData={editUser} onSubmit={handleUpdate}
              loading={editLoading} error={editError} isEdit roles={roles} />
          </ModalBody>
          <ModalFooter />
        </ModalContent>
      </Modal>

      {/* Modal Eliminar usuario */}
      <Modal isOpen={isDeleteOpen} onClose={closeDelete} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Eliminar usuario</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              ¿Estás seguro que deseas eliminar a{' '}
              <strong>
                {deleteUser
                  ? `${deleteUser.first_name || ''} ${deleteUser.last_name || ''}`.trim() || deleteUser.username
                  : ''}
              </strong>
              ? Esta acción no se puede deshacer.
            </Text>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button size="sm" variant="ghost" onClick={closeDelete}>Cancelar</Button>
              <Button size="sm" colorScheme="red" onClick={handleDelete} isLoading={deleteLoading}>
                Eliminar
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default SistemaUsuarios;
